import React, { useState, useEffect } from 'react';
import { useContent } from '../../contexts/ContentContext';
import ImageUpload from './ImageUpload';
import { validateYouTubeUrl, extractYouTubeVideoId, generateYouTubeThumbnail } from '../../utils/youtubeUtils';
import './TestimonialsAdmin.css';

const TestimonialsAdmin = ({ contentPath }) => {
  const { getContent, updateContent } = useContent();
  const [sectionData, setSectionData] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const isParticipantTestimonials = contentPath.includes('participantTestimonials');
  const isExpertTestimonials = contentPath.includes('expertTestimonials');

  useEffect(() => {
    const data = getContent(contentPath);
    setSectionData(data);
  }, [contentPath, getContent]);

  const handleSectionUpdate = (field, value) => {
    updateContent(`${contentPath}.${field}`, value);
    setSectionData(getContent(contentPath));
  };

  const handleTestimonialUpdate = (index, field, value) => {
    updateContent(`${contentPath}.testimonials.${index}.${field}`, value);
    setSectionData(getContent(contentPath));
  };

  const handleAddTestimonial = () => {
    const testimonials = sectionData?.testimonials || [];
    const newTestimonial = {
      id: `${isParticipantTestimonials ? 'participant' : 'expert'}-${Date.now()}`,
      name: '',
      title: '',
      formation: '',
      photo: null,
      text: '',
      type: '',
      // Video-specific fields
      youtube_url: '',
      youtube_id: '',
      video_thumbnail: '',
      ...(isParticipantTestimonials && {
        location: '',
        rating: 5,
        date: new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
      }),
      ...(isExpertTestimonials && {
        specialties: []
      })
    };

    // Add new testimonial at the TOP of the list
    const updatedTestimonials = [newTestimonial, ...testimonials];
    updateContent(`${contentPath}.testimonials`, updatedTestimonials);
    setSectionData(getContent(contentPath));
    setShowAddForm(false);
    
    // Automatically open the new testimonial for editing (it's now at index 0)
    setEditingItem(0);
    
    // Scroll to the top of the testimonials section
    setTimeout(() => {
      const testimonialsSection = document.querySelector('.testimonials-grid-admin');
      if (testimonialsSection) {
        testimonialsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleDeleteTestimonial = (index) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce témoignage ?')) {
      const testimonials = sectionData?.testimonials || [];
      const updatedTestimonials = testimonials.filter((_, i) => i !== index);
      updateContent(`${contentPath}.testimonials`, updatedTestimonials);
      setSectionData(getContent(contentPath));
      setEditingItem(null);
    }
  };

  const handleSpecialtiesUpdate = (index, specialties) => {
    const specialtiesArray = specialties.split(',').map(s => s.trim()).filter(s => s.length > 0);
    handleTestimonialUpdate(index, 'specialties', specialtiesArray);
  };

  const handleTypeChange = (index, type) => {
    handleTestimonialUpdate(index, 'type', type);
    
    // Clear type-specific fields when switching types
    if (type === 'text') {
      handleTestimonialUpdate(index, 'youtube_url', '');
      handleTestimonialUpdate(index, 'youtube_id', '');
      handleTestimonialUpdate(index, 'video_thumbnail', '');
    } else if (type === 'video') {
      handleTestimonialUpdate(index, 'text', '');
    }
  };

  const handleYouTubeUrlChange = (index, url) => {
    handleTestimonialUpdate(index, 'youtube_url', url);
    
    if (validateYouTubeUrl(url)) {
      const videoId = extractYouTubeVideoId(url);
      const thumbnail = generateYouTubeThumbnail(videoId);
      
      handleTestimonialUpdate(index, 'youtube_id', videoId);
      handleTestimonialUpdate(index, 'video_thumbnail', thumbnail);
    } else {
      handleTestimonialUpdate(index, 'youtube_id', '');
      handleTestimonialUpdate(index, 'video_thumbnail', '');
    }
  };


  if (!sectionData) {
    return (
      <div className="testimonials-admin-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div className="testimonials-admin">
      {/* Section Header Settings */}
      <div className="admin-section-header">
        <div className="section-info">
          <h2>
            <i className={`fas ${isParticipantTestimonials ? 'fa-users' : 'fa-user-tie'}`}></i>
            {isParticipantTestimonials ? 'Témoignages des Participants' : 'Témoignages des Experts'}
          </h2>
          <p>Gérez les témoignages qui apparaissent sur votre site web</p>
        </div>
        <button 
          className="btn-add-testimonial"
          onClick={() => setShowAddForm(true)}
        >
          <i className="fas fa-plus"></i>
          Ajouter un témoignage
        </button>
      </div>

      {/* Section Title & Subtitle */}
      <div className="section-settings">
        <div className="setting-group">
          <label>Titre de la section</label>
          <input
            type="text"
            value={sectionData.title || ''}
            onChange={(e) => handleSectionUpdate('title', e.target.value)}
            placeholder={isParticipantTestimonials ? 'Témoignages de nos Participants' : 'Témoignages de nos Experts'}
          />
        </div>
        <div className="setting-group">
          <label>Sous-titre</label>
          <textarea
            value={sectionData.subtitle || ''}
            onChange={(e) => handleSectionUpdate('subtitle', e.target.value)}
            placeholder="Description de la section..."
            rows={2}
          />
        </div>
      </div>

      {/* Testimonials Grid */}
      <div className="testimonials-grid-admin">
        {(sectionData.testimonials || []).map((testimonial, index) => (
          <div key={testimonial.id} className="testimonial-card-admin">
            {/* Card Header with Actions */}
            <div className="card-header">
              <div className="card-meta">
                <span className="card-id">#{testimonial.id}</span>
                <span className={`card-type ${testimonial.type || 'pending'}`}>
                  <i className={`fas ${!testimonial.type ? 'fa-question-circle' : testimonial.type === 'video' ? 'fa-video' : 'fa-comment'}`}></i>
                  {!testimonial.type ? 'À définir' : testimonial.type === 'video' ? 'Vidéo' : 'Texte'}
                </span>
              </div>
              <div className="card-actions">
                <button
                  className={`btn-edit ${editingItem === index ? 'active' : ''}`}
                  onClick={() => setEditingItem(editingItem === index ? null : index)}
                  title={editingItem === index ? 'Arrêter l\'édition' : 'Modifier'}
                >
                  <i className={`fas ${editingItem === index ? 'fa-times' : 'fa-edit'}`}></i>
                </button>
                <button
                  className="btn-delete"
                  onClick={() => handleDeleteTestimonial(index)}
                  title="Supprimer"
                >
                  <i className="fas fa-trash"></i>
                </button>
              </div>
            </div>

            {/* Card Content */}
            <div className="card-content">
              {/* Photo Section */}
              <div className="photo-section">
                <div className="photo-preview">
                  {testimonial.photo ? (
                    <img src={testimonial.photo} alt={testimonial.name} />
                  ) : (
                    <div className="photo-placeholder">
                      <i className={`fas ${isParticipantTestimonials ? 'fa-user-doctor' : 'fa-user-tie'}`}></i>
                    </div>
                  )}
                </div>
                {editingItem === index && (
                  <div className="photo-upload">
                    <ImageUpload
                      value={testimonial.photo}
                      onChange={(imageUrl) => handleTestimonialUpdate(index, 'photo', imageUrl)}
                      label="Photo du profil"
                      placeholder="Télécharger une photo"
                    />
                  </div>
                )}
              </div>

              {/* Info Section */}
              <div className="info-section">
                {editingItem === index ? (
                  // Editing Mode
                  <div className="edit-form">
                    <div className="form-row">
                      <div className="form-field">
                        <label>Nom complet</label>
                        <input
                          type="text"
                          value={testimonial.name || ''}
                          onChange={(e) => handleTestimonialUpdate(index, 'name', e.target.value)}
                          placeholder="Dr. Nom Prénom"
                        />
                      </div>
                      <div className="form-field">
                        <label>Titre/Profession</label>
                        <input
                          type="text"
                          value={testimonial.title || ''}
                          onChange={(e) => handleTestimonialUpdate(index, 'title', e.target.value)}
                          placeholder="Chirurgien-Dentiste"
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-field">
                        <label>Formation/Spécialité</label>
                        <input
                          type="text"
                          value={testimonial.formation || ''}
                          onChange={(e) => handleTestimonialUpdate(index, 'formation', e.target.value)}
                          placeholder="Formation suivie..."
                        />
                      </div>
                      <div className="form-field">
                        <label>Type</label>
                        <select
                          value={testimonial.type || ''}
                          onChange={(e) => handleTypeChange(index, e.target.value)}
                        >
                          <option value="">-- Choisir le type --</option>
                          <option value="text">Témoignage texte</option>
                          <option value="video">Témoignage vidéo</option>
                        </select>
                      </div>
                    </div>

                    {isParticipantTestimonials && (
                      <div className="form-row">
                        <div className="form-field">
                          <label>Localisation</label>
                          <input
                            type="text"
                            value={testimonial.location || ''}
                            onChange={(e) => handleTestimonialUpdate(index, 'location', e.target.value)}
                            placeholder="Ville"
                          />
                        </div>
                        <div className="form-field">
                          <label>Date</label>
                          <input
                            type="text"
                            value={testimonial.date || ''}
                            onChange={(e) => handleTestimonialUpdate(index, 'date', e.target.value)}
                            placeholder="Mois Année"
                          />
                        </div>
                        <div className="form-field">
                          <label>Note</label>
                          <select
                            value={testimonial.rating || 5}
                            onChange={(e) => handleTestimonialUpdate(index, 'rating', parseInt(e.target.value))}
                          >
                            <option value={5}>⭐⭐⭐⭐⭐ (5)</option>
                            <option value={4}>⭐⭐⭐⭐ (4)</option>
                            <option value={3}>⭐⭐⭐ (3)</option>
                          </select>
                        </div>
                      </div>
                    )}

                    {isExpertTestimonials && (
                      <div className="form-field">
                        <label>Spécialités (séparées par des virgules)</label>
                        <input
                          type="text"
                          value={(testimonial.specialties || []).join(', ')}
                          onChange={(e) => handleSpecialtiesUpdate(index, e.target.value)}
                          placeholder="Implantologie, Orthodontie, Endodontie..."
                        />
                      </div>
                    )}

                    {/* Content Section - Text or Video */}
                    {!testimonial.type ? (
                      <div className="form-field full-width">
                        <div style={{
                          padding: '2rem',
                          textAlign: 'center',
                          background: '#f8fafc',
                          border: '2px dashed #d1d5db',
                          borderRadius: '8px',
                          color: '#6b7280'
                        }}>
                          <i className="fas fa-arrow-up" style={{ fontSize: '2rem', marginBottom: '1rem' }}></i>
                          <p style={{ margin: 0, fontWeight: '500' }}>
                            Veuillez d'abord choisir le type de témoignage ci-dessus
                          </p>
                        </div>
                      </div>
                    ) : testimonial.type === 'text' ? (
                      <div className="form-field full-width">
                        <label>Témoignage</label>
                        <textarea
                          value={testimonial.text || ''}
                          onChange={(e) => handleTestimonialUpdate(index, 'text', e.target.value)}
                          placeholder="Le contenu du témoignage..."
                          rows={4}
                        />
                        <span className="char-count">{(testimonial.text || '').length} caractères</span>
                      </div>
                    ) : (
                      <div className="form-field full-width">
                        <label>
                          <i className="fab fa-youtube" style={{ color: '#ff0000' }}></i>
                          URL YouTube
                        </label>
                        <input
                          type="url"
                          value={testimonial.youtube_url || ''}
                          onChange={(e) => handleYouTubeUrlChange(index, e.target.value)}
                          placeholder="https://www.youtube.com/watch?v=..."
                          style={{
                            border: testimonial.youtube_url && !validateYouTubeUrl(testimonial.youtube_url) ? '2px solid #ef4444' : '1px solid #d1d5db'
                          }}
                        />
                        {testimonial.youtube_url && !validateYouTubeUrl(testimonial.youtube_url) && (
                          <span style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                            <i className="fas fa-exclamation-triangle"></i> URL YouTube invalide
                          </span>
                        )}
                        {testimonial.youtube_url && validateYouTubeUrl(testimonial.youtube_url) && (
                          <div style={{ marginTop: '1rem', padding: '1rem', background: '#f0f9ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                              <img 
                                src={testimonial.video_thumbnail} 
                                alt="Video thumbnail"
                                style={{
                                  width: '120px',
                                  height: '90px',
                                  objectFit: 'cover',
                                  borderRadius: '4px',
                                  border: '1px solid #d1d5db'
                                }}
                                onError={(e) => {
                                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjkwIiB2aWV3Qm94PSIwIDAgMTIwIDkwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTIwIiBoZWlnaHQ9IjkwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik00OCA0MEw3MiA1NUw0OCA3MFY0MFoiIGZpbGw9IiM2QjcyODAiLz4KPC9zdmc+';
                                }}
                              />
                              <div>
                                <p style={{ margin: '0 0 5px 0', fontWeight: '500', color: '#1f2937' }}>
                                  <i className="fas fa-check-circle" style={{ color: '#10b981' }}></i>
                                  Vidéo YouTube détectée
                                </p>
                                <p style={{ margin: '0', fontSize: '0.8rem', color: '#6b7280' }}>
                                  ID: {testimonial.youtube_id}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  // Display Mode
                  <div className="display-info">
                    <h3>{testimonial.name || 'Nom non défini'}</h3>
                    <p className="title">{testimonial.title}</p>
                    <p className="formation">{testimonial.formation}</p>
                    
                    {isParticipantTestimonials && (
                      <div className="participant-meta">
                        <span className="location">
                          <i className="fas fa-map-marker-alt"></i>
                          {testimonial.location} • {testimonial.date}
                        </span>
                        <div className="rating">
                          {[...Array(testimonial.rating || 5)].map((_, i) => (
                            <i key={i} className="fas fa-star"></i>
                          ))}
                        </div>
                      </div>
                    )}

                    {isExpertTestimonials && testimonial.specialties && (
                      <div className="specialties">
                        {testimonial.specialties.map((specialty, i) => (
                          <span key={i} className="specialty-tag">{specialty}</span>
                        ))}
                      </div>
                    )}

                    {/* Content Display - Text or Video */}
                    {testimonial.type === 'video' && testimonial.youtube_id ? (
                      <div className="video-testimonial">
                        <div 
                          className="youtube-video-container"
                          onClick={() => {
                            window.open(testimonial.youtube_url, '_blank');
                          }}
                          style={{
                            position: 'relative',
                            cursor: 'pointer',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            marginTop: '1rem'
                          }}
                        >
                          <img 
                            src={testimonial.video_thumbnail} 
                            alt={`Témoignage vidéo de ${testimonial.name}`}
                            style={{
                              width: '100%',
                              maxWidth: '400px',
                              height: '225px',
                              objectFit: 'cover'
                            }}
                            onError={(e) => {
                              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjkwIiB2aWV3Qm94PSIwIDAgMTIwIDkwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTIwIiBoZWlnaHQ9IjkwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik00OCA0MEw3MiA1NUw0OCA3MFY0MFoiIGZpbGw9IiM2QjcyODAiLz4KPC9zdmc+';
                            }}
                          />
                          <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            color: '#ff0000',
                            fontSize: '3rem',
                            textShadow: '0 2px 8px rgba(0,0,0,0.8)',
                            transition: 'transform 0.2s ease'
                          }}>
                            <i className="fab fa-youtube"></i>
                          </div>
                          <div style={{
                            position: 'absolute',
                            bottom: '8px',
                            right: '8px',
                            background: 'rgba(0,0,0,0.8)',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '0.8rem',
                            fontWeight: '500'
                          }}>
                            <i className="fab fa-youtube"></i> Vidéo
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="testimonial-text">
                        <p>{testimonial.text || 'Aucun témoignage saisi'}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {/* Add New Testimonial Card */}
        {showAddForm && (
          <div className="testimonial-card-admin add-card">
            <div className="card-header">
              <h3>
                <i className="fas fa-plus"></i>
                Nouveau témoignage
              </h3>
              <button
                className="btn-cancel"
                onClick={() => setShowAddForm(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="card-content">
              <p>Cliquez sur "Confirmer" pour créer un nouveau témoignage que vous pourrez ensuite modifier.</p>
              <div className="add-actions">
                <button className="btn-confirm" onClick={handleAddTestimonial}>
                  <i className="fas fa-check"></i>
                  Confirmer
                </button>
                <button className="btn-cancel-secondary" onClick={() => setShowAddForm(false)}>
                  Annuler
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Empty State */}
      {(!sectionData.testimonials || sectionData.testimonials.length === 0) && !showAddForm && (
        <div className="empty-state">
          <div className="empty-icon">
            <i className={`fas ${isParticipantTestimonials ? 'fa-users' : 'fa-user-tie'}`}></i>
          </div>
          <h3>Aucun témoignage</h3>
          <p>Commencez par ajouter votre premier témoignage pour cette section.</p>
          <button className="btn-add-first" onClick={() => setShowAddForm(true)}>
            <i className="fas fa-plus"></i>
            Ajouter le premier témoignage
          </button>
        </div>
      )}
    </div>
  );
};

export default TestimonialsAdmin;
